import { Tooltip } from '../../../../components/Tooltip';
import type { SurveySummaryBar } from '../surveyTypes';

/**
 * A horizontal bar chart of how many gave each answer — Responses › Summary.
 *
 * Built to the dataviz method, in plain HTML:
 *
 * - **Form**: magnitude across categories with long names → horizontal bars from one baseline.
 * - **Color**: ONE series, so ONE hue and no legend — `blue-500`, validated (lightness, chroma,
 *   contrast vs the light and dark surfaces all pass). Blue, never the brand yellow: this is a
 *   fact about records, and yellow marks the product's own chrome.
 * - **Marks**: a THIN bar — 8px, rounded at both ends — on a track of the same shape (product
 *   call, 09 Oct 2026; it was 20px with one rounded end). The hover target stays the full row.
 * - **Labels**: every bar carries its count and share as TEXT in the ink tokens, never the series
 *   color — few enough bars that this is the table view, not clutter — so nothing depends on color.
 * - **Hover**: a tooltip per bar, over a hit target the full row tall rather than the bar alone.
 */
export function SurveyBarChart({ bars, total, label }: { bars: SurveySummaryBar[]; total: number; label: string }) {
  const max = Math.max(1, ...bars.map((b) => b.count));

  return (
    <ul aria-label={label} className="flex flex-col gap-2">
      {bars.map((bar) => {
        const share = total > 0 ? Math.round((bar.count / total) * 100) : 0;
        const width = (bar.count / max) * 100;
        const summary = `${bar.label}: ${bar.count} of ${total} (${share}%)`;

        return (
          <li key={bar.label} className="grid grid-cols-[minmax(0,12rem)_minmax(0,1fr)_5.5rem] items-center gap-3">
            {/* A long option ellipsizes; the whole of it rides in the tooltip, never a `title`. */}
            <Tooltip label={bar.label} block>
              <span className="block truncate text-sm text-slate-600">{bar.label}</span>
            </Tooltip>

            <Tooltip label={summary} block>
              {/* The hit target is the whole row height; the bar inside it is the mark. */}
              <span className="flex h-7 items-center" aria-hidden="true">
                <span className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  {bar.count > 0 ? (
                    <span
                      className="block h-2 rounded-full bg-blue-500"
                      style={{ width: `${width}%`, minWidth: 8 }}
                    />
                  ) : null}
                </span>
              </span>
            </Tooltip>

            {/* A zero is muted, never a dash — the value is none, not absent. */}
            <span className={`text-right font-mono text-xs tabular-nums ${bar.count === 0 ? 'text-slate-300' : 'text-slate-600'}`}>
              {bar.count} <span className="text-slate-400">({share}%)</span>
            </span>

            <span className="sr-only">{summary}</span>
          </li>
        );
      })}
    </ul>
  );
}
